/* Search303 repair 2026-09-11, dependencies update.
 * Keeps the repaired viewport placement, cancellation and keyboard behaviour.
 * Requires the companion predictive-search.liquid with explicit stylesheet tags.
 */
(() => {
  'use strict';

  const VERSION = '2026-09-11-repair-2-dependencies';
  const previous = window.Search303;
  if (previous?.version === VERSION) return;
  if (typeof previous?.destroy === 'function') previous.destroy();

  let instance = null;
  const lifecycle = new AbortController();

  function initialise() {
    const drawer = document.getElementById('Search303');
    if (!drawer || instance?.drawer === drawer) return;
    instance?.destroy();

    const backdrop = document.getElementById('Search303Backdrop');
    const input = drawer.querySelector('#Search303Input');
    const closeButton = drawer.querySelector('#Search303Close');
    const results = drawer.querySelector('#Search303Results');
    const defaults = drawer.querySelector('#Search303Default');
    const form = input?.form;
    if (!backdrop || !input || !closeButton || !results || !form) {
      console.error('[Search303] Missing markup. Use the existing complete Search303 block.');
      return;
    }

    // CRITICAL: escape the header's stacking context and fixed containing block.
    // Appending below </header> in a section is not the same as appending to <body>.
    const home = document.createComment('Search303 original section position');
    drawer.before(home);
    document.body.append(backdrop, drawer);

    const listeners = new AbortController();
    const on = (element, name, callback, options = {}) => {
      element.addEventListener(name, callback, { ...options, signal: listeners.signal });
    };
    const pl = (document.documentElement.lang || 'pl').toLowerCase().startsWith('pl');
    const labels = pl ? {
      search: 'Wyszukiwanie produktow', loading: 'Ladowanie wynikow...',
      error: 'Nie udalo sie wczytac wynikow.', retry: 'Sprobuj ponownie',
      full: 'Otworz pelne wyniki', count: 'Liczba pokazanych produktow: '
    } : {
      search: 'Product search', loading: 'Loading results...',
      error: 'Unable to load results.', retry: 'Try again',
      full: 'Open full results', count: 'Number of products shown: '
    };
    // Polish labels are assigned as Unicode below, keeping source portability.
    if (pl) Object.assign(labels, {
      search: 'Wyszukiwanie produkt\u00f3w', loading: '\u0141adowanie wynik\u00f3w\u2026',
      error: 'Nie uda\u0142o si\u0119 wczyta\u0107 wynik\u00f3w.',
      retry: 'Spr\u00f3buj ponownie', full: 'Otw\u00f3rz pe\u0142ne wyniki',
      count: 'Liczba pokazanych produkt\u00f3w: '
    });

    let status = drawer.querySelector('[data-search303-status]');
    if (!status) {
      status = document.createElement('div');
      status.className = 'search303__status';
      status.dataset.search303Status = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      drawer.append(status);
    }

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', labels.search);
    drawer.setAttribute('tabindex', '-1');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open', 'has-query');
    drawer.inert = true;
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.classList.remove('is-open');
    input.setAttribute('aria-label', input.getAttribute('aria-label') || input.placeholder || labels.search);
    input.setAttribute('aria-controls', 'Search303Results');
    results.setAttribute('role', 'region');
    results.setAttribute('aria-label', labels.search);
    results.setAttribute('aria-busy', 'false');
    results.hidden = true;
    closeButton.type = 'button';

    let opened = false;
    let returnFocus = null;
    let debounceTimer = null;
    let request = null;
    let revision = 0;
    let composing = false;
    let pageInert = [];
    const loadedStyles = new Set();
    const styleJobs = new Map();
    const scriptJobs = new Map();

    function trustedThemeAsset(url) {
      if (!['https:', 'http:'].includes(url.protocol)) return false;
      const normaliseHost = value => value.toLowerCase().replace(/^www\./, '');
      if (normaliseHost(url.hostname) === normaliseHost(location.hostname)) return true;
      if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com')) return true;
      // A store's canonical asset hostname can differ from the current market URL.
      // Only accept other origins already used for Shopify theme assets on this page.
      return Array.from(document.querySelectorAll('link[rel="stylesheet"][href], script[src]')).some(node => {
        try {
          const known = new URL(node.getAttribute('href') || node.getAttribute('src'), location.href);
          return known.origin === url.origin && /\/cdn\/shop\/|\/s\/files\//.test(known.pathname);
        } catch (_) { return false; }
      });
    }

    async function installProductForm(inner, responseURL) {
      if (!inner.querySelector('product-form') || customElements.get('product-form')) return;
      const src = inner.getAttribute('data-search303-product-form-src');
      if (!src) throw new Error('The results section is missing its product-form dependency.');
      const url = new URL(src, responseURL);
      // Do not execute arbitrary scripts from the fetched HTML.
      if (!trustedThemeAsset(url) || !/\/product-form\.js$/.test(url.pathname)) {
        throw new Error('Unexpected product-form asset URL.');
      }
      if (!scriptJobs.has(url.href)) {
        const job = new Promise((resolve, reject) => {
          let script = Array.from(document.scripts).find(node => node.src === url.href);
          const created = !script;
          if (!script) {
            script = document.createElement('script');
            script.src = url.href;
            script.async = true;
            script.dataset.search303Dependency = 'product-form';
          }
          let finished = false;
          let timeout;
          function finish(error) {
            if (finished) return;
            finished = true;
            window.clearTimeout(timeout);
            script.removeEventListener('load', loaded);
            script.removeEventListener('error', failed);
            if (error) {
              if (created) script.remove();
              reject(error);
            } else resolve();
          }
          function loaded() {
            if (customElements.get('product-form')) finish();
            else finish(new Error('product-form.js did not register its custom element.'));
          }
          function failed() { finish(new Error('Unable to load product-form.js.')); }
          timeout = window.setTimeout(() => finish(new Error('product-form.js timed out.')), 10000);
          script.addEventListener('load', loaded);
          script.addEventListener('error', failed);
          customElements.whenDefined('product-form').then(() => finish());
          if (created) document.head.append(script);
          else if (customElements.get('product-form')) finish();
        });
        scriptJobs.set(url.href, job);
        job.catch(() => scriptJobs.delete(url.href));
      }
      await scriptJobs.get(url.href);
    }

    function triggers() {
      return document.querySelectorAll('[data-search303-open]');
    }
    function triggerState(value) {
      triggers().forEach(trigger => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', 'Search303');
        trigger.setAttribute('aria-expanded', String(value));
      });
    }
    triggerState(false);

    function cancelPending() {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      revision += 1;
      request?.abort();
      request = null;
      results.setAttribute('aria-busy', 'false');
    }

    function syncEmptyState(term) {
      const hasQuery = Boolean(term);
      drawer.classList.toggle('has-query', hasQuery);
      results.hidden = !hasQuery;
      if (defaults) defaults.hidden = hasQuery;
    }

    function setPageInert(enabled) {
      if (enabled) {
        pageInert = [];
        for (const child of document.body.children) {
          if (child === drawer || child === backdrop ||
              ['SCRIPT', 'STYLE', 'LINK'].includes(child.tagName)) continue;
          pageInert.push([child, child.inert]);
          child.inert = true;
        }
      } else {
        pageInert.forEach(([child, wasInert]) => { child.inert = wasInert; });
        pageInert = [];
      }
    }

    function open(trigger) {
      if (opened) { input.focus({ preventScroll: true }); return; }
      // Reassert placement after a theme editor update.
      if (drawer.parentNode !== document.body || backdrop.parentNode !== document.body) {
        document.body.append(backdrop, drawer);
      }
      returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      opened = true;
      drawer.inert = false;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      backdrop.classList.add('is-open');
      document.documentElement.classList.add('search303-open');
      triggerState(true);
      setPageInert(true);
      input.value = '';
      results.replaceChildren();
      status.textContent = '';
      syncEmptyState('');
      window.requestAnimationFrame(() => {
        if (opened) input.focus({ preventScroll: true });
      });
    }

    function close(restoreFocus = true) {
      if (!opened) return;
      opened = false;
      cancelPending();
      drawer.classList.remove('is-open', 'has-query');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.inert = true;
      backdrop.classList.remove('is-open');
      document.documentElement.classList.remove('search303-open');
      triggerState(false);
      setPageInert(false);
      input.value = '';
      results.replaceChildren();
      status.textContent = '';
      syncEmptyState('');
      if (restoreFocus && returnFocus?.isConnected && typeof returnFocus.focus === 'function') {
        returnFocus.focus({ preventScroll: true });
      }
    }

    function fullSearchURL(term) {
      const url = new URL(form.getAttribute('action') || '/search', location.href);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url;
    }

    function queryURL(term) {
      const root = window.Shopify?.routes?.root || '/';
      const base = new URL(root.endsWith('/') ? root : `${root}/`, location.href);
      const url = new URL('search/suggest', base);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');
      return url;
    }

    // Do not discard CSS that Shopify returns outside the results wrapper.
    // Execute no scripts from an AJAX response.
    async function installResponseStyles(doc, responseURL) {
      const jobs = [];
      for (const element of doc.querySelectorAll('style, link[rel="stylesheet"]')) {
        if (element.tagName === 'STYLE') {
          const text = element.textContent;
          if (!text?.trim() || loadedStyles.has(text)) continue;
          const style = document.createElement('style');
          style.dataset.search303ResponseStyle = '';
          if (element.media) style.media = element.media;
          style.textContent = text;
          document.head.append(style);
          loadedStyles.add(text);
          continue;
        }
        const href = element.getAttribute('href');
        if (!href) continue;
        const url = new URL(href, responseURL);
        if (!trustedThemeAsset(url)) {
          throw new Error(`Untrusted stylesheet URL: ${url.hostname}`);
        }
        if (styleJobs.has(url.href)) { jobs.push(styleJobs.get(url.href)); continue; }
        const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
          .find(link => link.href === url.href && link.sheet);
        if (existing) continue;
        const job = new Promise((resolve, reject) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = url.href;
          link.dataset.search303ResponseStyle = '';
          const timeout = window.setTimeout(() => {
            link.remove();
            reject(new Error(`Stylesheet timeout: ${url.pathname}`));
          }, 10000);
          link.onload = () => { window.clearTimeout(timeout); resolve(); };
          link.onerror = () => {
            window.clearTimeout(timeout);
            link.remove();
            reject(new Error(`Stylesheet unavailable: ${url.pathname}`));
          };
          document.head.append(link);
        });
        styleJobs.set(url.href, job);
        job.catch(() => styleJobs.delete(url.href));
        jobs.push(job);
      }
      await Promise.all(jobs);
    }

    function showError(term) {
      results.replaceChildren();
      const box = document.createElement('div');
      box.className = 'search303-empty';
      const message = document.createElement('p');
      message.textContent = labels.error;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'search303__retry';
      retry.textContent = labels.retry;
      retry.addEventListener('click', handleInput, { once: true });
      const link = document.createElement('a');
      link.href = fullSearchURL(term).href;
      link.textContent = labels.full;
      box.append(message, retry, document.createTextNode(' '), link);
      results.append(box);
      status.textContent = labels.error;
    }

    async function fetchResults(term, ticket) {
      if (!opened || ticket !== revision || input.value.trim() !== term) return;
      const controller = new AbortController();
      request = controller;
      results.setAttribute('aria-busy', 'true');
      status.textContent = labels.loading;
      const timeout = window.setTimeout(() => controller.abort('timeout'), 15000);
      try {
        const response = await fetch(queryURL(term), {
          credentials: 'same-origin', signal: controller.signal,
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('.search303-results-inner');
        if (!inner || /Liquid (?:error|syntax error)/i.test(inner.textContent)) {
          throw new Error('Expected Search303 section markup was not returned.');
        }
        const responseURL = response.url || queryURL(term).href;
        await installResponseStyles(doc, responseURL);
        if (!opened || ticket !== revision || input.value.trim() !== term || controller.signal.aborted) return;
        try {
          // Size buttons in the native card use product-form even with quick add off.
          await installProductForm(inner, responseURL);
        } catch (dependencyError) {
          // Keep product links usable rather than show uninitialised cart buttons.
          console.warn('[Search303] Size buttons disabled:', dependencyError);
          inner.querySelectorAll('.card-product--size-options').forEach(node => node.remove());
        }
        if (!opened || ticket !== revision || input.value.trim() !== term || controller.signal.aborted) return;
        // Trusted theme HTML only, with scripts removed. Never insert an entire page fallback.
        const section = doc.getElementById('shopify-section-predictive-search');
        const markup = section?.contains(inner) ? section : inner;
        markup.querySelectorAll('script, style, link[rel="stylesheet"]').forEach(node => node.remove());
        // Keep the section wrapper: returned CSS may target its ID.
        results.replaceChildren(document.importNode(markup, true));
        results.scrollTop = 0;
        // Results are navigable links, not ARIA-selectable combobox options.
        results.querySelectorAll('.search303-grid').forEach(list => list.setAttribute('role', 'list'));
        results.querySelectorAll('.search303-product').forEach(item => item.setAttribute('role', 'listitem'));
        const count = results.querySelectorAll('.search303-product').length;
        status.textContent = count ? labels.count + count : (results.textContent || '').trim();
      } catch (error) {
        if (!opened || ticket !== revision || input.value.trim() !== term) return;
        if (controller.signal.aborted && controller.signal.reason !== 'timeout') return;
        console.error('[Search303]', error);
        showError(term);
      } finally {
        window.clearTimeout(timeout);
        if (request === controller) request = null;
        if (ticket === revision) results.setAttribute('aria-busy', 'false');
      }
    }

    function handleInput() {
      cancelPending(); // Abort immediately, including when cleared or still debouncing.
      const term = input.value.trim();
      syncEmptyState(term);
      results.replaceChildren();
      status.textContent = '';
      if (!opened || !term || composing) return;
      const ticket = revision;
      debounceTimer = window.setTimeout(() => fetchResults(term, ticket), 180);
    }

    on(input, 'input', handleInput);
    on(input, 'compositionstart', () => { composing = true; cancelPending(); });
    on(input, 'compositionend', () => { composing = false; handleInput(); });
    on(form, 'submit', event => {
      if (!input.value.trim()) { event.preventDefault(); return; }
      const type = form.querySelector('input[name="type"]');
      if (type) type.value = 'product';
    });
    on(closeButton, 'click', event => { event.preventDefault(); close(); });
    on(backdrop, 'click', () => close());
    // The transparent full-screen root can receive the outside click, too.
    on(drawer, 'click', event => { if (event.target === drawer) close(); });

    on(window, 'click', event => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-search303-open]') : null;
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open(trigger);
    }, { capture: true });

    on(document, 'keydown', event => {
      if (!opened) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(el => el.tabIndex >= 0 && !el.closest('[hidden], [inert]') &&
        el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const first = items[0];
      const last = items[items.length - 1];
      if (!first) { event.preventDefault(); drawer.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }, { capture: true });

    const controller = {
      drawer, home, open, close,
      destroy() {
        close(false);
        cancelPending();
        listeners.abort();
        drawer.inert = false;
        // Restore initial location for a harmless script reload.
        if (home.isConnected) home.replaceWith(backdrop, drawer);
        else { backdrop.remove(); drawer.remove(); }
      }
    };
    instance = controller;
  }

  const api = {
    version: VERSION,
    open() { initialise(); instance?.open(); },
    close() { instance?.close(); },
    destroy() { lifecycle.abort(); instance?.destroy(); instance = null; }
  };
  window.Search303 = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true, signal: lifecycle.signal });
  } else initialise();
  document.addEventListener('shopify:section:load', initialise, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:unload', event => {
    if (instance && event.target.contains(instance.home)) {
      instance.destroy();
      instance = null;
    }
  }, { signal: lifecycle.signal });
})();
